/**
 * Merge proposal data into comprehensive clauses
 *
 * This ensures that clauses reflect the LATEST proposed/accepted values,
 * not just the original contract values.
 */

import { ComprehensiveClauseCategory } from './comprehensiveClauseMapping';

export interface Proposal {
  _id: string;
  contractId: string;
  clauseId: string;
  clauseHierarchicalId?: string;
  originalValue: any;
  proposedValue: any;
  status:
    | 'pending'
    | 'accepted'
    | 'rejected'
    | 'countered'
    | 'accepted_pending_ratification'
    | 'accepted_and_applied';
  proposedBy: string;
  proposedByUser: string;
  responseRequiredFrom: string;
  message?: string;
  createdAt: string;
  respondedAt?: string;
}

/**
 * Merges proposals into comprehensive clauses to show negotiated values
 *
 * Logic:
 * 1. If a proposal is accepted/accepted_pending_ratification, use proposedValue as the clause value
 * 2. If a proposal is pending and user should see it, show it as a badge/note
 * 3. Keep originalValue as the contract's original value
 */
export function mergeProposalsIntoClauses(
  categories: ComprehensiveClauseCategory[],
  proposals: Proposal[],
  userRole: 'buyer' | 'seller' | 'agent'
): ComprehensiveClauseCategory[] {
  if (!proposals || proposals.length === 0) {
    console.log('No proposals to merge');
    return categories;
  }

  console.log(
    `\n🔄 Merging ${proposals.length} proposals into clauses for ${userRole}`
  );

  // Create a map of clauseId → latest accepted/pending proposal
  const proposalMap = new Map<string, Proposal>();

  proposals.forEach((proposal) => {
    const existing = proposalMap.get(proposal.clauseId);

    // Prioritize accepted proposals
    if (
      proposal.status === 'accepted' ||
      proposal.status === 'accepted_pending_ratification'
    ) {
      if (!existing || existing.status !== 'accepted') {
        proposalMap.set(proposal.clauseId, proposal);
        console.log(
          `  ✓ Mapping ${
            proposal.clauseId
          } to ACCEPTED proposal: ${JSON.stringify(proposal.proposedValue)}`
        );
      }
    }
    // Then pending proposals
    else if (proposal.status === 'pending' && !existing) {
      proposalMap.set(proposal.clauseId, proposal);
      console.log(
        `  ⏳ Mapping ${
          proposal.clauseId
        } to PENDING proposal: ${JSON.stringify(proposal.proposedValue)}`
      );
    }
  });

  // Merge proposals into clauses. Enriched clauses carry extra negotiation
  // metadata (proposalStatus, hasAcceptedProposal, ...) beyond the declared
  // clause shape, hence the cast rather than a stricter return type here.
  return categories.map((category) => ({
    ...category,
    clauses: category.clauses.map((clause) => {
      const proposal = proposalMap.get(clause.id);

      if (!proposal) {
        // No proposal for this clause, keep original
        return clause;
      }

      // If proposal is accepted, update the clause value to the proposed value
      if (
        proposal.status === 'accepted' ||
        proposal.status === 'accepted_pending_ratification'
      ) {
        console.log(
          `  📝 Updating ${clause.id} value: ${clause.value} → ${proposal.proposedValue} (ACCEPTED)`
        );
        return {
          ...clause,
          value: proposal.proposedValue, // Use the accepted proposed value
          originalValue: clause.originalValue, // Keep original for reference
          proposalStatus: proposal.status,
          proposedBy: proposal.proposedBy,
          proposalMessage: proposal.message,
          hasAcceptedProposal: true,
        };
      }

      // If proposal is pending, add metadata but keep original value
      if (proposal.status === 'pending') {
        return {
          ...clause,
          proposalStatus: proposal.status,
          proposedValue: proposal.proposedValue,
          proposedBy: proposal.proposedBy,
          proposalMessage: proposal.message,
          hasPendingProposal: true,
        };
      }

      return clause;
    }),
  })) as ComprehensiveClauseCategory[];
}

/**
 * Helper to get the effective value for a clause
 * (considers accepted proposals)
 */
export function getEffectiveClauseValue(
  clause: any,
  proposals: Proposal[]
): any {
  const acceptedProposal = proposals.find(
    (p) =>
      p.clauseId === clause.id &&
      (p.status === 'accepted' || p.status === 'accepted_pending_ratification')
  );

  return acceptedProposal ? acceptedProposal.proposedValue : clause.value;
}
